import type { FilterExpression, FilterGroup } from "./types.js";
import { getDynamicValueLabel, getOperatorArity, getOperatorLabel, normalizeOperator } from "./operators.js";
import { isExpression } from "./tree.js";
import type { DynamicValue, FieldMetadata } from "./types.js";

export type DescribeFilterOptions = {
	/** Field metadata, used to resolve captions and date operator wording. */
	fields?: FieldMetadata[];
};

/**
 * Builds a human-readable description of a filter, without contacting the
 * server. Use this for read-only summaries; the authoritative filter string is
 * produced by `api/filter/convert`, since only the server can generate the
 * DevExpress criteria syntax.
 */
export function describeFilter(
	filter: FilterGroup | FilterExpression | null | undefined,
	options: DescribeFilterOptions = {},
): string {
	if (!filter) return "";

	let fieldsByName = new Map((options.fields ?? []).map((field) => [field.name.toLowerCase(), field]));

	return describe(filter, fieldsByName, 0);
}

function describe(item: FilterGroup | FilterExpression, fields: Map<string, FieldMetadata>, depth: number): string {
	if (isExpression(item)) return describeExpression(item, fields);

	let parts = item.items.map((child) => describe(child, fields, depth + 1)).filter((part) => part !== "");

	if (parts.length === 0) return "";
	if (parts.length === 1) return parts[0] as string;

	let separator = item.mode === "or" ? " OR " : " AND ";
	let joined = parts.join(separator);

	return depth === 0 ? joined : `(${joined})`;
}

function describeExpression(expression: FilterExpression, fields: Map<string, FieldMetadata>): string {
	if (!expression.column || !expression.operator) return "";

	let field = fields.get(expression.column.toLowerCase());
	let caption = field?.caption ?? expression.column;
	let operator = normalizeOperator(expression.operator);
	let label = getOperatorLabel(operator, field?.type);
	let arity = getOperatorArity(operator);

	if (arity === "none") return `[${caption}] ${label}`;

	return `[${caption}] ${label} ${formatValue(expression, arity)}`;
}

function formatValue(expression: FilterExpression, arity: ReturnType<typeof getOperatorArity>): string {
	let { value, valueType } = expression;

	if (valueType === "special" && typeof value === "string") {
		return getDynamicValueLabel(value as DynamicValue);
	}

	if (Array.isArray(value)) {
		let formatted = value.map((item) => formatScalar(item, valueType));

		return arity === "pair" ? formatted.join(" - ") : `(${formatted.join(", ")})`;
	}

	return formatScalar(value, valueType);
}

function formatScalar(value: unknown, valueType: string): string {
	if (value === null || value === undefined) return "";
	if (value instanceof Date) return value.toISOString().slice(0, 10);
	if (typeof value === "number" || typeof value === "boolean") return String(value);
	if (valueType === "number" || valueType === "boolean") return String(value);

	return `'${String(value)}'`;
}
